import { useCallback, useState } from "react"
import { Nav, OverlayTrigger, Tooltip } from "react-bootstrap"

import { faGear, faPlus, faPrint } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ModalWithInput } from "modules/Shared/components/TabModals"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import {
  DEFAULT_SPEED_DATE_RANGE,
  DUPLICATE_SELECTED_TAB,
  GetCurrentDate,
  NEW_TAB,
  PrevetState,
  REMOVE_SELECTED_TAB,
  RENAME_SELECTED_TAB,
  SELECT_TAB,
} from "../hooks/prevetStateReducer"
import { ConstructPrevetTab } from "./PravetTabs"
import { PrevetMain } from "./PrevetMain"
import PrevetPortAlertModal from "./PrevetPortAlertModal"

export function PrevetTabStateMain() {
  const [currentPrevetTabStateName, setCurrentPrevetTabStateName] = useState("")
  const [showDuplicate, setShowDuplicate] = useState(false)
  const [showRename, setShowRename] = useState(false)

  const {
    reducerPrevetTabState,
    getSelectedIndex,
    setTabStateValue,
    prevetTabStateDispatch,
    isPortAlertAdmin,
    showPortAlertModal,
    setShowPortAlertModal,
  } = usePrevetTabStateContext()

  const newHandler = () => {
    prevetTabStateDispatch({ type: NEW_TAB })
  }

  const renameHandler = useCallback(
    async (newName: string): Promise<void> => {
      try {
        if (reducerPrevetTabState) {
          const exist = reducerPrevetTabState.tabStates.find(
            (i) =>
              i.tabName === newName && i.id !== getSelectedIndex() && !i.deleted
          )
          if (exist) {
            return Promise.reject("Duplicate name")
          }
          prevetTabStateDispatch({
            type: RENAME_SELECTED_TAB,
            payload: { selectedIndex: getSelectedIndex(), name: newName },
          })
          return Promise.resolve()
        }
      } catch (err) {
        setShowRename(false)
        setCurrentPrevetTabStateName(newName)
        setShowRename(true)
        throw err
      }
    },
    [reducerPrevetTabState]
  )

  const duplicateHandler = useCallback(
    async (duplicateName: string): Promise<void> => {
      try {
        prevetTabStateDispatch({
          type: DUPLICATE_SELECTED_TAB,
          payload: getSelectedIndex(),
        })
        return Promise.resolve()
      } catch (err) {
        setShowDuplicate(false)
        setCurrentPrevetTabStateName(duplicateName)
        setShowDuplicate(true)
        throw err
      }
    },
    [reducerPrevetTabState]
  )

  const closeTabHandler = useCallback(
    async (id: number): Promise<void> => {
      prevetTabStateDispatch({ type: REMOVE_SELECTED_TAB, payload: id })
    },
    [reducerPrevetTabState]
  )

  const clearTabHandler = useCallback(
    async (id: number): Promise<void> => {
      setTabStateValue("imo", undefined, undefined)
      setTabStateValue("date", GetCurrentDate(), undefined)
      setTabStateValue("deleted", false, undefined)
      setTabStateValue("cvdLadenSpeed", "", undefined)
      setTabStateValue("cvdLadenFuelCon", "", undefined)
      setTabStateValue("cvdLadenSeaDays", "", undefined)
      setTabStateValue("cvdBallastSpeed", "", undefined)
      setTabStateValue("cvdBallastFuelCon", "", undefined)
      setTabStateValue("cvdBallastSeaDays", "", undefined)
      setTabStateValue("vesselSpeedGraphZoneType", undefined, undefined)
      setTabStateValue(
        "vesselSpeedDateRange",
        DEFAULT_SPEED_DATE_RANGE,
        undefined
      )
      setTabStateValue("perfExposureHireRate", "", undefined)
      setTabStateValue("perfExposureFuelPrice", "", undefined)
      setTabStateValue("perfExposureValue", "", undefined)
      setTabStateValue("hvIncludeBallast", true, undefined)
      setTabStateValue("hvIncludeLaden", true, undefined)
      setTabStateValue("hvSearchText", "", undefined)
      setTabStateValue("hvCurrentPage", 1, undefined)
      setTabStateValue("hvRowsPerPage", 10, undefined)
    },
    [reducerPrevetTabState]
  )

  const modalDecorator = useCallback(
    (wrappedFunc: void) => {
      const prevetTabStateName =
        (reducerPrevetTabState &&
        reducerPrevetTabState.tabStates[getSelectedIndex()]
          ? reducerPrevetTabState.tabStates[getSelectedIndex()].tabName
          : "") || ""
      setCurrentPrevetTabStateName(prevetTabStateName)
      return wrappedFunc
    },
    [reducerPrevetTabState]
  )

  return (
    <>
      {/* modal */}
      <PrevetPortAlertModal
        show={showPortAlertModal}
        handleClose={() => {
          setShowPortAlertModal(false)
        }}
      />
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Nav
          variant="tabs"
          defaultActiveKey={"0"}
          onSelect={(selectedIndex: string | null) => {
            // Do not dispatch if selectedKey is not number - from command menus such "new", "rename" and etc.
            if (selectedIndex && !isNaN(Number(selectedIndex))) {
              prevetTabStateDispatch({
                type: SELECT_TAB,
                payload: +selectedIndex,
              })
            }
          }}
          activeKey={getSelectedIndex()}
          key="mainNav"
        >
          {ConstructPrevetTab(
            reducerPrevetTabState
              ? reducerPrevetTabState.tabStates
              : ([] as PrevetState[]),
            getSelectedIndex(),
            () => modalDecorator(setShowRename(true)),
            () => modalDecorator(setShowDuplicate(true)),
            closeTabHandler,
            clearTabHandler
          )}
          <button
            style={{
              marginLeft: "5px", // Space between tab and +
              background: "none", // No background
              border: "none", // No border
              fontSize: "1.2rem", // Font size for better visibility
              cursor: "pointer", // Pointer cursor on hover
            }}
            onClick={newHandler}
          >
            <FontAwesomeIcon icon={faPlus} className="fa-thin" size="xs" />
          </button>
        </Nav>

        <div style={{ paddingRight: "20px" }}>
          <div className="d-flex gap-2 align-items-center">
            {isPortAlertAdmin && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="button-tooltip">Edit port alerts</Tooltip>
                }
              >
                <button
                  style={{
                    border: "none", // No border
                    cursor: "pointer", // Pointer cursor on hover
                  }}
                  onClick={() => setShowPortAlertModal(true)}
                >
                  <FontAwesomeIcon
                    icon={faGear}
                    className="fa-thin"
                    size="lg"
                  />
                </button>
              </OverlayTrigger>
            )}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="save-print">Save/Print</Tooltip>}
            >
              <button
                style={{
                  border: "none", // No border
                  cursor: "pointer", // Pointer cursor on hover
                }}
                onClick={captureScreen}
              >
                <FontAwesomeIcon icon={faPrint} className="fa-thin" size="lg" />
              </button>
            </OverlayTrigger>
          </div>
        </div>
      </div>
      <PrevetMain selectedIndex={getSelectedIndex()} />
      {
        // Rename modal dialog box
        ModalWithInput(
          showRename,
          currentPrevetTabStateName,
          "Update tab name",
          "New tab name",
          renameHandler,
          () => setShowRename(false)
        )
      }
      {
        // Duplicate modal dialog box
        ModalWithInput(
          showDuplicate,
          currentPrevetTabStateName + " copy",
          "Duplicate tab",
          "Tab name",
          duplicateHandler,
          () => setShowDuplicate(false)
        )
      }
    </>
  )
}

// Function to capture the screen and either print or save as PDF
async function captureScreen(): Promise<void> {
  try {
    window.print()
  } catch (error) {
    console.error("Error capturing the screen:", error)
  }
}
